import { db } from './config';
import { doc, getDoc, collection, addDoc, setDoc, Timestamp, GeoPoint, query, where, getDocs } from 'firebase/firestore';

/**
 * Manually syncs a newly created site to the sitesearch_beta collection
 * This ensures newly created sites appear on the enhanced map immediately
 * @param {string} siteId - The ID of the site to sync
 */
export const syncNewSiteToBeta = async (siteId) => {
  try {
    // Get the site data
    const siteDoc = await getDoc(doc(db, 'sites', siteId));
    
    if (!siteDoc.exists()) {
      console.error('Site not found for beta sync:', siteId);
      return { success: false, error: 'Site not found' };
    }
    
    // Get site data
    const siteData = siteDoc.data();
    
    // Enhanced coordinate handling and logging
    let geoPoint = null;
    console.log(`[syncNewSiteToBeta] Processing coordinates for site ${siteId}:`, 
      JSON.stringify(siteData.location?.coordinates || 'No coordinates'));
    
    // Handle both formats: {lat, lng} and {latitude, longitude}
    const rawLat = siteData.location?.coordinates?.lat || siteData.location?.coordinates?.latitude;
    const rawLng = siteData.location?.coordinates?.lng || siteData.location?.coordinates?.longitude;
    
    if (rawLat != null && rawLng != null) {
      try {
        const lat = Number(rawLat);
        const lng = Number(rawLng);
        console.log(`[syncNewSiteToBeta] Extracted coords for ${siteId}: lat=${lat}, lng=${lng} (types: ${typeof lat}, ${typeof lng})`);
        
        if (!isNaN(lat) && !isNaN(lng)) {
          geoPoint = new GeoPoint(lat, lng);
          console.log(`[syncNewSiteToBeta] GeoPoint created for ${siteId}`);
        } else {
          console.warn(`[syncNewSiteToBeta] Invalid coordinates for site ${siteId}: lat=${rawLat}, lng=${rawLng} converted to NaN`);
        }
      } catch (coordError) {
        console.error(`[syncNewSiteToBeta] Error creating GeoPoint for site ${siteId}:`, coordError);
      }
    } else {
      console.warn(`[syncNewSiteToBeta] Coordinates missing or incomplete for site ${siteId}. Raw values: Lat: ${rawLat}, Lng: ${rawLng}`);
    }
    
    // Function to normalize feature IDs consistently
    const normalizeFeature = (featureId) => {
      const str = String(featureId || '');
      return str.toLowerCase()
        .replace(/[^a-z0-9]+/g, '')
        .trim();
    };
    
    // Get site media for features extraction
    const mediaItems = siteData.media || [];
    
    // Get all unique normalized features from all media
    const allNormalizedFeatures = new Set();
    
    // Prepare feature index
    const featureIndex = {};
    
    mediaItems.forEach(media => {
      if (!Array.isArray(media.predictions)) return;
      
      media.predictions.forEach(pred => {
        if (pred.source === 'auto' && pred.confidence <= 0.5) return;
        
        const normalizedId = normalizeFeature(pred.class);
        if (!normalizedId) return;
        
        allNormalizedFeatures.add(normalizedId);
        
        if (!featureIndex[normalizedId]) {
          featureIndex[normalizedId] = {
            count: 0,
            mediaIds: [],
            confidence: 0
          };
        }
        
        featureIndex[normalizedId].count++;
        featureIndex[normalizedId].mediaIds.push(media.id);
        featureIndex[normalizedId].confidence += (pred.confidence || 0.75);
      });
    });
    
    // Calculate average confidence for each feature
    Object.keys(featureIndex).forEach(feature => {
      featureIndex[feature].confidence = 
        featureIndex[feature].confidence / featureIndex[feature].count;
    });
    
    // Prepare map preview media
    const mapPreviewMedia = mediaItems
      .filter(media => media.type === 'image' && media.url)
      .slice(0, 3)
      .map(media => ({
        id: media.id,
        url: media.url,
        featureTags: Array.isArray(media.predictions) 
          ? media.predictions
            .filter(p => !(p.source === 'auto' && p.confidence <= 0.5))
            .map(p => normalizeFeature(p.class))
            .filter(Boolean)
          : []
      }));
    
    // Get main image
    const mainImage = siteData.mainImage || 
                     (mediaItems.find(media => media.type === 'image' && media.url)?.url) || 
                     null;
    
    // Calculate map priority score
    const mapPriority = 50; // Default priority for new sites
    
    // Fetch and prepare sublocations data
    const mapSublocations = [];
    try {
      console.log(`[syncNewSiteToBeta] Fetching sublocations for site ${siteId}`);
      const sublocationsQuery = query(
        collection(db, 'site_sublocations'),
        where('siteId', '==', siteId)
      );
      
      const sublocationsSnapshot = await getDocs(sublocationsQuery);
      
      // Process sublocations with coordinate normalization
      sublocationsSnapshot.docs.forEach(doc => {
        const sublocationData = doc.data();
        
        // Check if coordinates exist and normalize them
        if (sublocationData.coordinates) {
          const coords = sublocationData.coordinates;
          // Handle different coordinate formats
          const lat = coords.latitude !== undefined ? Number(coords.latitude) : 
                      coords.lat !== undefined ? Number(coords.lat) : null;
          const lng = coords.longitude !== undefined ? Number(coords.longitude) : 
                      coords.lng !== undefined ? Number(coords.lng) : null;
                      
          if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
            mapSublocations.push({
              id: doc.id,
              name: sublocationData.name || '',
              type: sublocationData.type || 'default',
              coordinates: {
                lat: lat,
                lng: lng
              }
            });
          } else {
            console.warn(`[syncNewSiteToBeta] Invalid coordinates for sublocation ${doc.id}:`, coords);
          }
        } else {
          console.warn(`[syncNewSiteToBeta] Sublocation ${doc.id} has no coordinates`);
        }
      });
      
      console.log(`[syncNewSiteToBeta] Processed ${mapSublocations.length} sublocations for site ${siteId}`);
    } catch (sublocError) {
      console.error(`[syncNewSiteToBeta] Error fetching sublocations for site ${siteId}:`, sublocError);
    }
    
    // Prepare zoom level data
    const zoomLevelData = {
      high: {
        name: siteData.name,
        culture: siteData.culture || '',
        era: siteData.startEra || siteData.estimatedEra || '',
        mainImageUrl: mainImage,
        featureCount: allNormalizedFeatures.size,
        hasSublocations: mapSublocations.length > 0
      },
      medium: {
        name: siteData.name
      },
      low: {
        id: siteId
      }
    };
    
    // Prepare optimized beta search document
    const searchBetaData = {
      // Basic site info
      id: siteId,
      name: siteData.name || '',
      description: siteData.description || '',
      location: {
        address: siteData.location?.address || '',
        coordinates: geoPoint,
        region: siteData.location?.region || '',
        country: siteData.location?.country || ''
      },
      
      // Map-specific data
      mapPriority: mapPriority,
      mapPreviewMedia: mapPreviewMedia,
      sublocationCount: mapSublocations.length,
      mapSublocations: mapSublocations,
      zoomLevelData: zoomLevelData,
      
      // Media data
      mainImageUrl: mainImage,
      mainImagePredictions: [],
      media: mediaItems.slice(0, 5).map(m => ({
        id: m.id,
        url: m.url,
        type: m.type,
        predictions: m.predictions || []
      })),
      mediaCount: mediaItems.length,
      
      // Feature data
      features_normalized: Array.from(allNormalizedFeatures),
      featureCount: allNormalizedFeatures.size,
      featureIndex: featureIndex,
      
      // Feature-media mapping
      featureMediaMap: Object.fromEntries(
        Object.entries(featureIndex).map(([feature, data]) => 
          [feature, data.mediaIds.slice(0, 3)]
        )
      ),
      
      // Zoom-specific feature data
      zoomFeatureData: {
        low: {
          hasFeatures: allNormalizedFeatures.size > 0
        },
        medium: {
          featureCount: allNormalizedFeatures.size,
          topFeatures: Object.entries(featureIndex)
            .sort((a, b) => b[1].confidence - a[1].confidence)
            .slice(0, 3)
            .map(([feature]) => feature)
        }
      },
      
      // Basic metadata
      culture: siteData.culture || '',
      startEra: siteData.startEra || '',
      endEra: siteData.endEra || '',
      featured: !!siteData.featured,
      
      // Timestamps
      createdAt: siteData.createdAt || new Date(),
      updatedAt: new Date(),
      
      // Search metadata
      hasDetails: !!(siteData.description && siteData.description.length > 100),
      lastMediaUpdate: siteData.lastMediaUpdate || null,
      materialType: siteData.materialType || ''
    };
    
    // Add geohashing for efficient spatial queries
    if (geoPoint) {
      try {
        const lat = geoPoint.latitude;
        const lng = geoPoint.longitude;
        const precision = 5; // ~5km precision
        
        // Generate simple quadrant identifiers for grid-based querying
        const latGrid = Math.floor((lat + 90) / 180 * Math.pow(2, precision));
        const lngGrid = Math.floor((lng + 180) / 360 * Math.pow(2, precision));
        
        searchBetaData.geoHash = `${latGrid}:${lngGrid}`;
        
        // Add regional clustering identifiers
        searchBetaData.regionCode = `${Math.floor(lat)}:${Math.floor(lng)}`;
        searchBetaData.clusterGroup = `${Math.floor(lat/10)}:${Math.floor(lng/10)}`;
      } catch (error) {
        console.error(`Error generating geohash for site ${siteId}:`, error);
      }
    }
    
    // Save the optimized search document
    await setDoc(doc(db, 'sitesearch_beta', siteId), searchBetaData);
    
    console.log('Successfully synced new site to beta collection:', siteId);
    return { success: true };
    
  } catch (error) {
    console.error('Error syncing site to beta collection:', error);
    return { success: false, error: error.message };
  }
};
